import React, { useState, useEffect } from "react";
import {
  User,
  Trophy,
  Star,
  Calendar,
  Share2,
  Copy,
  Check,
  ArrowLeft,
  Shield,
  TrendingUp,
  Target,
  Mail,
} from "lucide-react";
import { useParams, Link, useNavigate } from "react-router-dom";
import FootballLoading from "../../Utils/FootballLoading";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const StatCard = ({ icon: Icon, label, value, color = "red" }) => {
  return (
    <div
      className={`bg-gray-800/70 rounded-lg p-3 flex flex-col items-center justify-center`}
    >
      <div
        className={`p-2 rounded-full bg-${color}-500/20 text-${color}-400 mb-1`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-lg font-bold text-white">{value}</div>
      <div className="text-xs text-gray-400">{label}</div>
    </div>
  );
};

const AchievementBadge = ({ icon: Icon, label }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-gradient-to-br from-gray-700 to-gray-900 p-2 rounded-lg border border-gray-700 mb-1">
        <Icon className="w-6 h-6 text-yellow-400" />
      </div>
      <span className="text-xs text-gray-300">{label}</span>
    </div>
  );
};

const PublicProfile = () => {
  const { userId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!userId) {
          throw new Error("ID de usuario no proporcionado");
        }

        setLoading(true);
        const db = getFirestore();
        const userRef = doc(db, "users", userId);
        const userSnapshot = await getDoc(userRef);

        if (!userSnapshot.exists()) {
          throw new Error("Perfil no encontrado");
        }

        setProfileData({ id: userSnapshot.id, ...userSnapshot.data() });
        setShareUrl(window.location.href);
      } catch (err) {
        console.error("Error al cargar el perfil:", err);
        setError(err.message || "Error al cargar el perfil de usuario");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userId]);

  // Copy URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Handle profile sharing
  const handleShareProfile = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Perfil de ${profileData?.name || "Usuario"}`,
          text: "¡Mira este perfil en ToWin!",
          url: shareUrl,
        })
        .catch((err) => console.log("Error al compartir:", err));
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
        <FootballLoading />
        <p className="mt-4 text-white text-opacity-80 animate-pulse">
          Cargando perfil...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 p-3">
        <div className="text-center bg-red-900/30 p-4 rounded-lg border border-red-800 max-w-md">
          <div className="text-red-500 text-5xl mb-3">!</div>
          <h2 className="text-lg font-bold text-white mb-2">Error</h2>
          <p className="text-red-400 mb-3 text-sm">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-red-600 text-white py-1.5 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </button>
        </div>
      </div>
    );
  }

  const isVip = profileData?.typeU === "Cuenta Vip";
  const joinDate = profileData?.createdAt
    ? new Date(profileData.createdAt).toLocaleDateString()
    : "No disponible";

  // Calculated stats
  const winRate =
    profileData?.predictions && profileData?.correctPredictions
      ? Math.round(
          (profileData.correctPredictions / profileData.predictions) * 100
        )
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-lg mx-auto">
        {/* Back button */}
        <div className="p-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </button>
        </div>

        {/* Profile Card */}
        <div className="bg-gray-900 rounded-xl shadow-2xl border border-gray-800 overflow-hidden">
          {/* Header */}
          <div className="relative">
            {/* Banner with design */}
            <div className="h-28 bg-gradient-to-r from-red-600 to-orange-500 relative overflow-hidden">
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-0 right-0 h-full w-full bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.15),transparent_70%)]"></div>
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute rounded-full bg-white/10"
                    style={{
                      width: `${Math.random() * 30 + 10}px`,
                      height: `${Math.random() * 30 + 10}px`,
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                      animation: `float ${
                        Math.random() * 10 + 5
                      }s ease-in-out infinite`,
                    }}
                  ></div>
                ))}
              </div>

              {/* Share Button */}
              <button
                onClick={handleShareProfile}
                className="absolute top-3 right-3 bg-white/20 hover:bg-white/30 text-white p-1.5 rounded-full transition-all duration-300 backdrop-blur-sm"
                title="Compartir perfil"
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Share2 className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Avatar and name */}
            <div className="flex flex-col items-center -mt-14 pb-3">
              <div
                className={`relative border-4 ${
                  isVip ? "border-yellow-500" : "border-gray-900"
                } rounded-full shadow-xl`}
              >
                <img
                  src="https://avatar.iran.liara.run/public/boy"
                  alt="Profile"
                  className="w-28 h-28 rounded-full object-cover"
                />
                {isVip && (
                  <div className="absolute -top-1 -right-1 bg-yellow-500 text-yellow-900 p-1 rounded-full">
                    <Star className="w-4 h-4 fill-yellow-900" />
                  </div>
                )}
              </div>

              <div className="mt-3 text-center px-3">
                <h1 className="text-2xl font-bold text-white">
                  {profileData?.name || "Sin identificar"}
                </h1>

                {/* Account Type Badge */}
                <div
                  className={`mt-1 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center ${
                    isVip
                      ? "bg-gradient-to-r from-yellow-600 to-yellow-400 text-yellow-900"
                      : "bg-red-500/20 text-red-400"
                  }`}
                >
                  {isVip ? (
                    <Trophy className="w-3 h-3 mr-1" />
                  ) : (
                    <User className="w-3 h-3 mr-1" />
                  )}
                  {profileData?.typeU || "Usuario"}
                  {isVip && <span className="ml-1">✨</span>}
                </div>

                {/* Join Date */}
                <div className="text-xs text-gray-400 mt-1 flex items-center justify-center">
                  <Calendar className="w-3 h-3 mr-1" />
                  Miembro desde {joinDate}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Stats */}
          <div className="px-3 pb-4">
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <StatCard
                icon={Target}
                label="Pronósticos"
                value={profileData?.predictions || 0}
              />
              <StatCard
                icon={TrendingUp}
                label="Acertados"
                value={profileData?.correctPredictions || 0}
                color="green"
              />
              <StatCard
                icon={Trophy}
                label="% Acierto"
                value={`${winRate}%`}
                color="yellow"
              />
            </div>

            {/* Achievements Section */}
            {isVip && (
              <>
                <div className="mb-2 text-sm font-medium text-white flex items-center">
                  <Shield className="w-4 h-4 mr-1 text-yellow-400" />
                  Logros desbloqueados
                </div>
                <div className="grid grid-cols-4 gap-2 mb-4 bg-gray-800/50 p-2 rounded-lg">
                  <AchievementBadge icon={Star} label="VIP" />
                  <AchievementBadge icon={Target} label="Experto" />
                  <AchievementBadge icon={User} label="Referido" />
                  <AchievementBadge icon={Trophy} label="Ganador" />
                </div>
              </>
            )}

            {/* Referral Code */}
            {profileData?.referralCode && (
              <div className="bg-gray-800/70 p-3 rounded-lg mb-4">
                <div className="text-sm font-medium text-white mb-1">
                  Código de referido
                </div>
                <div className="flex items-center justify-between bg-gray-900 p-2 rounded">
                  <div className="text-lg font-mono text-red-400">
                    {profileData.referralCode}
                  </div>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(profileData.referralCode);
                      alert("Código copiado");
                    }}
                    className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Contact Section */}
            <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
              <div className="text-sm font-medium text-white mb-2 flex items-center">
                <Mail className="w-4 h-4 mr-1 text-red-400" />
                ¿Quieres contactar a este usuario?
              </div>
              <Link
                to="/contact"
                className="bg-red-600 hover:bg-red-700 text-white w-full py-2 px-3 rounded-lg transition-colors text-sm font-medium text-center block"
              >
                Contáctanos para más información
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
